import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { InteractionContextType, InteractionState } from '../types';
import {
    createGridInteractionStore,
    type GridInteractionStore,
} from './gridInteractionStore';
import { useGridInteractionState } from './useGridInteractionState';

// @preserved_behavior: the compatibility context remains in this module for HMR stability.
const GridInteractionContext = createContext<InteractionContextType | undefined>(undefined);
const GridInteractionStoreContext = createContext<GridInteractionStore | undefined>(undefined);

const selectWholeState = (state: InteractionState) => state;

function GridInteractionCompatibilityProvider({
    children,
    store,
}: {
    children: ReactNode;
    store: GridInteractionStore;
}) {
    const state = useGridInteractionState(store, selectWholeState);
    const value = useMemo<InteractionContextType>(() => ({
        ...state,
        ...store.actions,
    }), [state, store]);

    return (
        <GridInteractionContext.Provider value={value}>
            {children}
        </GridInteractionContext.Provider>
    );
}

export function GridInteractionProvider({ children }: { children: ReactNode }) {
    const [store] = useState(createGridInteractionStore);

    useEffect(() => () => {
        store.cancelPendingTimers();
    }, [store]);

    return (
        <GridInteractionStoreContext.Provider value={store}>
            <GridInteractionCompatibilityProvider store={store}>
                {children}
            </GridInteractionCompatibilityProvider>
        </GridInteractionStoreContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGridInteractionStore(): GridInteractionStore {
    const store = useContext(GridInteractionStoreContext);
    if (store === undefined) {
        throw new Error('useGridInteractionStore must be used within a GridInteractionProvider');
    }
    return store;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGridInteraction(): InteractionContextType {
    const context = useContext(GridInteractionContext);
    if (context === undefined) {
        throw new Error('useGridInteraction must be used within a GridInteractionProvider');
    }
    return context;
}
